import { Form, Input } from "antd"
import { Button, DashboardContainer, H5, P2 } from "components"
import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { CustomInput, CustomSelect } from "components/new-stuff/form"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { IState, ICountry } from "pages/dashboard/people/people/typings"
import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import FormSectionWrapper from "components/new-stuff/form/FormSectionWrapper"
import { invalidateCompanySetupInfoQueries } from "pages/dashboard/wallets/integration/utils/invalidateCompanySetupInfoQueries"
import GeoPicker from "components/GeoPicker"
import { parseGooglePlace } from "utils/googleAddress"
import { companyService, COMPANY_ENDPOINTS } from "services/companyService"
import { commonService, COMMON_ENDPOINTS } from "services/commonService"
import { onQueryError } from "utils/utils"
import { getCurrencyCodeFromAlpha2 } from "utils/currency"
import { useAppNotificationStore } from "stores"

export const SSNIT_INSTITUTION_OPTIONS = [
  { label: "Ghana Tier 2 Fund Manager 1", value: "GH_T2_01" },
  { label: "Ghana Tier 2 Fund Manager 2", value: "GH_T2_02" },
]

const AddWorkplaceForm = () => {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const [alpha2Code, setAlpha2Code] = useState<string>("NG")
  const [stateCode, setStateCode] = useState<string>("")
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useAppNotificationStore()

  const [pendingState, setPendingState] = useState<{
    name: string
    short: string
  } | null>(null)

  const latitude = Form.useWatch("latitude", form)
  const longitude = Form.useWatch("longitude", form)
  const hasCoordinates = latitude != null && longitude != null

  const redirectToOnboarding =
    searchParams.get("redirect") === "onboarding-workplaces"

  const { isLoading: isCountriesLoading, data: countriesRes } = useQuery<
    IBaseRes<ICountry[]>
  >({
    queryKey: [COMMON_ENDPOINTS.GET_COUNTRIES],
    queryFn: () => commonService.getCountries(),
  })
  const countries = countriesRes?.data

  const {
    isLoading: isStatesLoading,
    data: statesRes,
    refetch,
  } = useQuery<IBaseRes<IState[]>>({
    queryKey: [COMMON_ENDPOINTS.GET_STATES_BY_COUNTRY, alpha2Code],
    queryFn: () => commonService.getStatesByCountry(alpha2Code),
    enabled: !!alpha2Code,
  })
  const states = statesRes?.data

  const taxOfficeCurrencyCode = getCurrencyCodeFromAlpha2(alpha2Code)

  const {
    isLoading: isTaxOfficesLoading,
    data: taxOfficesRes,
    refetch: refetchTaxOffices,
  } = useQuery<IBaseRes<{ description: string; code: string }[]>>({
    queryKey: [
      COMPANY_ENDPOINTS.GET_TAX_OFFICES_BY_CURRENCY_CODE,
      taxOfficeCurrencyCode,
    ],
    queryFn: () =>
      companyService.getTaxOfficesByCurrencyCode(taxOfficeCurrencyCode),
    enabled: !!taxOfficeCurrencyCode,
  })
  const taxOffices = taxOfficesRes?.data

  const {
    isLoading: isHousingLocationsLoading,
    data: housingLocationsRes,
    refetch: refetchHousingLocationsOffices,
  } = useQuery<IBaseRes<{ description: string; code: string }[]>>({
    queryKey: [COMPANY_ENDPOINTS.GET_HOUSING_LOCATIONS, stateCode],
    queryFn: () => companyService.getHousingLocations(stateCode),
    enabled: !!stateCode,
  })
  const housingLocations = housingLocationsRes?.data

  const { isLoading: isSubmitting, mutate: submit } = useMutation({
    mutationFn: (body: any) => companyService.addOfficeAddress(body),
    onSuccess: (data) => {
      toast.success(data?.message)
      queryClient.invalidateQueries({
        queryKey: [COMPANY_ENDPOINTS.VIEW_DETAILS],
      })
      queryClient.invalidateQueries({
        queryKey: [COMPANY_ENDPOINTS.GET_OFFICE_ADDRESSES],
      })

      if (redirectToOnboarding) {
        navigate("/dashboard/company-onboarding")
        invalidateCompanySetupInfoQueries(queryClient)
      } else {
        navigate("/dashboard/company-details/manage-workplace")
      }

      mixPanelEvent("company-add-workplace-success")
    },
    onError: onQueryError,
  })

  useEffect(() => {
    if (!pendingState || !states?.length) return
    const match = states.find(
      (s) =>
        s.description?.toLowerCase() === pendingState.name.toLowerCase() ||
        s.code?.toLowerCase() === pendingState.short.toLowerCase()
    )
    if (match) {
      form.setFieldsValue({ state: match.description })
      setStateCode(match.code)

      refetchTaxOffices()
      refetchHousingLocationsOffices()

      setPendingState(null)
    }
  }, [states, pendingState])

  const handlePlaceDetails = (place: any) => {
    const parsed = parseGooglePlace(place)

    const country = countries?.find((c) => c.alpha2Code === parsed.countryCode)

    form.setFieldsValue({
      addressLine1: parsed.addressLine1,
      city: parsed.city || "",
      zip: parsed.zip || "",
      taxOffice: "",
      housingLocation: "",
      ...(country ? { country: country.description } : {}),
    })

    if (country && country.alpha2Code !== alpha2Code) {
      setAlpha2Code(country.alpha2Code)
      setStateCode("")
      form.setFieldsValue({ state: "" })
    }

    if (parsed.stateName || parsed.stateShortName) {
      setPendingState({ name: parsed.stateName, short: parsed.stateShortName })
    }

    form.validateFields(["addressLine1"]).catch(() => {})
  }

  const onFinish = (values: any) => {
    const { addressLine1, addressLine2, city, zip, phone, displayName, taxId } =
      values
    const item = states?.find((state) => state.description === values.state)
    const ctry = countries?.find(
      (country) => country.description === values.country
    )
    const taxOffice = taxOffices?.find(
      (taxOffice) => taxOffice.description === values.taxOffice
    )

    const nhf = housingLocations?.find(
      (housingLocation) =>
        housingLocation.description === values.housingLocation
    )

    submit({
      office_address_line_1: addressLine1,
      office_address_line_2: addressLine2 || "",
      office_address_city: city || "",
      office_address_state_code: item?.code || "",
      office_address_zip_code: zip || "",
      office_address_country_code: ctry?.alpha2Code || "",
      office_address_phone: phone || "",
      display_name: displayName,
      tax_office_name: taxOffice?.description || "",
      tax_office_code: taxOffice?.code || "",
      tax_id: taxId,
      housing_location_name: nhf?.description || "",
      housing_location_code: nhf?.code || "",
      latitude: values.latitude != null ? String(values.latitude) : "",
      longitude: values.longitude != null ? String(values.longitude) : "",
      ssnit_institution_name:
        SSNIT_INSTITUTION_OPTIONS.find(
          (option) => option.value === values.institution
        )?.label || "",
      ssnit_institution_code: values.institution || "",
    })
    mixPanelEvent("company-add-workplace-init")
  }

  return (
    <DashboardContainer>
      <FormSectionWrapper
        isActive
        title={`Add workplace `}
        subtitle="To automate your payroll filings, we need to have your company’s accurate addresses. Please take a minute and enter them below."
        footerBtns={
          <div className="flex justify-end w-full gap-3">
            <Button
              color="neutral"
              type="button"
              onClick={(e) => {
                e.preventDefault()
                navigate("/dashboard/company-details/manage-workplace")
              }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={isSubmitting || !hasCoordinates}
              submitting={isSubmitting}
            >
              Save
            </Button>
          </div>
        }
        layout="vertical"
        form={form}
        onFinish={onFinish}
        autoComplete="off"
      >
        <div className="px-4 md:px-6 xl:px-[60px]">
          <div className="flex flex-col lg:flex-row lg:gap-10 ">
            <div className="w-full lg:w-1/2">
              <Form.Item
                label={
                  <span className="text-padeBlack font-circular text-sm">
                    Address line 1
                  </span>
                }
                name="addressLine1"
                rules={[
                  { required: true, message: "Please enter this field" },
                  ({ getFieldValue }) => ({
                    validator() {
                      const lat = getFieldValue("latitude")
                      const lng = getFieldValue("longitude")
                      if (lat != null && lng != null) return Promise.resolve()
                      return Promise.reject(
                        new Error(
                          "Please select an address from the suggestions"
                        )
                      )
                    },
                  }),
                ]}
              >
                <GeoPicker
                  onSelect={(lat, lng) => {
                    form.setFieldsValue({ latitude: lat, longitude: lng })
                    form.validateFields(["addressLine1"]).catch(() => {})
                  }}
                  onPlaceDetails={handlePlaceDetails}
                />
              </Form.Item>
              <Form.Item
                name="latitude"
                hidden
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="longitude"
                hidden
              >
                <Input />
              </Form.Item>
              <CustomInput
                label="Address line 2"
                name="addressLine2"
                maxLength={50}
              />
              <CustomInput
                label="City"
                name="city"
                hidden={!hasCoordinates}
              />
              {alpha2Code === "GH" && (
                <CustomSelect
                  label="SSNIT Institution"
                  name="institution"
                  required
                  hidden={!hasCoordinates}
                  options={SSNIT_INSTITUTION_OPTIONS}
                  placeholder="Please select"
                />
              )}
              <CustomSelect
                label="Country"
                name="country"
                required
                hidden={!hasCoordinates}
                onChange={(selected) => {
                  const country = countries?.find(
                    (country) => country.description === selected
                  )
                  setPendingState(null)
                  setAlpha2Code(country?.alpha2Code || "")
                  form.setFieldsValue({
                    state: "",
                    taxOffice: "",
                    housingLocation: "",
                    city: "",
                    zip: "",
                  })
                  refetch()
                }}
                isLoading={isCountriesLoading}
                options={
                  isCountriesLoading
                    ? []
                    : countries?.map((state) => ({
                        label: state.description,
                        value: state.description,
                      }))
                }
                placeholder="Please select"
              />
              {/* ---- States ---- */}
              <CustomSelect
                label="State/Province"
                name="state"
                required
                hidden={!hasCoordinates}
                onChange={(selected) => {
                  const state = states?.find(
                    (state) => state.description === selected
                  )
                  setPendingState(null)
                  setStateCode(state?.code || "")

                  form.setFieldsValue({
                    taxOffice: "",
                    housingLocation: "",
                    city: "",
                    zip: "",
                  })
                  refetchTaxOffices()
                  refetchHousingLocationsOffices()
                }}
                isLoading={isStatesLoading}
                options={
                  isStatesLoading
                    ? []
                    : states?.map((state) => ({
                        label: state.description,
                        value: state.description,
                      }))
                }
                placeholder="Please select"
              />
            </div>

            <div className="w-full lg:w-1/2">
              {/*  */}
              <CustomInput
                label="Zip Code"
                name="zip"
              />
              <CustomInput
                label="Phone"
                name="phone"
                required
                maxLength={15}
              />
              <CustomInput
                label="Display Name"
                name="displayName"
                rules={[
                  { required: true, message: "Please enter this field" },
                  {
                    max: 50,
                    message: "Display name cannot exceed 50 characters.",
                  },
                ]}
              />
              {/* ---- Tax Office ---- */}
              <CustomSelect
                label="Tax Office"
                name="taxOffice"
                isLoading={isTaxOfficesLoading}
                options={
                  isTaxOfficesLoading
                    ? []
                    : taxOffices?.map((state) => ({
                        label: state.description,
                        value: state.description,
                      }))
                }
                placeholder="Please select"
              />
              <CustomInput
                label="Tax ID"
                name={"taxId"}
              />
              <CustomSelect
                label="NHF Location"
                name="housingLocation"
                isLoading={isHousingLocationsLoading}
                options={
                  isHousingLocationsLoading
                    ? []
                    : housingLocations?.map((state) => ({
                        label: state.description,
                        value: state.description,
                      }))
                }
                placeholder="Please select"
              />
            </div>
          </div>
        </div>
      </FormSectionWrapper>
    </DashboardContainer>
  )
}

export default AddWorkplaceForm
